// ChecklistInlineItem.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './ChecklistInlineItem.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import TextInput from 'components/textinput/TextInput';

/**
 * ChecklistInlineItem
 *
 * Compact checklist item row for inline editing in the node form.
 * In readOnly: text stays; no drag, remove, toggle, or edit.
 *
 * @param {object} item - The checklist item to display.
 * @param {boolean} [readOnly=false] - Display-only.
 * @param {boolean} [hideStatus=false] - Hide checkbox when done state is unknown.
 * @param {boolean} isDragOver - Whether another item is hovering over this row.
 * @param {boolean} isDragging - Whether this row is the active drag source.
 * @param {boolean} isReorderEnabled - Whether drag reordering is available.
 * @param {function} onDragEnd - Clears the active drag state.
 * @param {function} onDragOver - Marks this row as the active drop target.
 * @param {function} onDragStart - Starts dragging this row.
 * @param {function} onDrop - Reorders the list when a dragged row is dropped here.
 * @param {function} onItemTextBlur - Persists the item text on blur.
 * @param {function} onItemTextChange - Updates local item text while typing.
 * @param {function} onRemoveItem - Removes the item from the checklist.
 * @param {function} onToggleItem - Toggles the item's completion status.
 *
 * @returns {ReactNode} The rendered checklist item row.
 */
const ChecklistInlineItem = ({
  item,
  readOnly = false,
  hideStatus = false,
  lockLabel = false,
  canRemove = true,
  isDragOver,
  isDragging,
  isReorderEnabled,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDrop,
  onItemTextBlur,
  onItemTextChange,
  onRemoveItem,
  onToggleItem,
}) => {
  const { theme } = useTheme();
  const isDone = String(item?.status || '').toLowerCase() === 'done';
  const textReadOnly = readOnly || lockLabel;

  return (
    <div
      className={[
        'checklist-inline-item',
        isDragging ? 'is-dragging' : '',
        isDragOver ? 'is-drag-over' : '',
        isReorderEnabled ? 'is-sortable' : '',
        readOnly ? 'is-readonly' : '',
      ].filter(Boolean).join(' ')}
      style={{
        backgroundColor: isDragOver ? theme.backgroundHover : 'transparent',
        opacity: isDragging ? 0.6 : 1,
      }}
      data-no-field-drag="true"
      onDragOver={readOnly ? undefined : (event) => onDragOver(item.key, event)}
      onDrop={readOnly ? undefined : (event) => onDrop(item.key, event)}
    >
      {!readOnly ? (
        <div
          className="checklist-inline-item-handle"
          draggable={isReorderEnabled}
          title={isReorderEnabled ? 'Drag to reorder' : 'Add more items to reorder'}
          onDragEnd={onDragEnd}
          onDragStart={(event) => onDragStart(item.key, event)}
        >
          <Icon
            iconKey="drag_indicator"
            width={14}
            height={14}
            color={isReorderEnabled ? theme.foregroundMuted : theme.backgroundMuted}
          />
        </div>
      ) : null}

      {!hideStatus ? (
        <div className={readOnly ? 'checklist-inline-item-checkbox--readonly' : undefined}>
          <Checkbox
            label=""
            size={20}
            value={isDone}
            onChange={readOnly ? undefined : () => onToggleItem(item.key)}
          />
        </div>
      ) : null}

      <TextInput
        className="checklist-inline-item-input"
        style={{
          borderRadius: 6,
          paddingLeft: 6,
          paddingTop: 3,
          cursor: readOnly ? 'default' : undefined,
        }}
        value={item.text || ''}
        placeholder={textReadOnly ? '' : 'Checklist item'}
        hybrid
        multiline
        expandable
        readOnly={textReadOnly}
        onBlur={textReadOnly ? undefined : (event) => onItemTextBlur(item.key, event.target.value)}
        onChange={textReadOnly ? undefined : (event) => onItemTextChange(item.key, event.target.value)}
      />

      {!readOnly && canRemove ? (
        <Button
          iconKey="do_not_disturb_on"
          iconSize={16}
          width="24px"
          backgroundColor="transparent"
          height="24px"
          onClick={() => onRemoveItem(item.key)}
        />
      ) : null}
    </div>
  );
};

ChecklistInlineItem.displayName = 'ChecklistInlineItem';

export default ChecklistInlineItem;
